import React, { useState, useEffect } from 'react';
import { X, Search } from 'lucide-react';
import { useApp } from '../context/AppContext';

const RELATIONSHIP_TYPES = [
  'Colleagues',
  'Friends',
  'Family',
  'Business Partners',
  'Business Acquaintance',
  'Classmates',
  'Neighbors',
  'Community Members',
  'Other'
];

function ConnectionForm({ contactId, existingConnectionIds, onClose, onSave }) {
  const { contacts, fetchContacts } = useApp();
  const [search, setSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [relationshipType, setRelationshipType] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContacts();
  }, []); // Only fetch once on mount

  const filteredContacts = contacts.filter(c => {
    if (c.id === contactId) return false;
    if (existingConnectionIds.includes(c.id)) return false;
    if (!search) return true;
    const searchLower = search.toLowerCase();
    const fullName = `${c.first_name} ${c.last_name || ''}`.toLowerCase();
    return fullName.includes(searchLower) ||
           (c.company && c.company.toLowerCase().includes(searchLower));
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedContact) return;

    setSaving(true);
    try {
      await onSave({
        contact_id_1: contactId,
        contact_id_2: selectedContact.id,
        relationship_type: relationshipType || null,
        notes: notes || null
      });
      onClose();
    } catch (error) {
      console.error('Error saving connection:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h2 className="modal-title">Add Connection</h2>
          <button className="btn btn-ghost" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Select Contact *</label>
              <div className="search-wrapper">
                <Search size={18} className="search-icon" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="form-input search-input"
                  placeholder="Search contacts..."
                />
              </div>

              {selectedContact && (
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem',
                  background: 'var(--gray-50)',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontWeight: '500' }}>
                      {selectedContact.first_name} {selectedContact.last_name}
                    </div>
                    {selectedContact.company && (
                      <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                        {selectedContact.company}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setSelectedContact(null)}
                  >
                    <X size={16} />
                  </button>
                </div>
              )}

              {!selectedContact && (
                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  marginTop: '0.5rem',
                  border: '1px solid var(--gray-200)',
                  borderRadius: '0.5rem'
                }}>
                  {filteredContacts.length === 0 ? (
                    <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--gray-500)' }}>
                      No contacts found
                    </div>
                  ) : (
                    filteredContacts.slice(0, 10).map(contact => (
                      <button
                        key={contact.id}
                        type="button"
                        onClick={() => setSelectedContact(contact)}
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          background: 'transparent',
                          border: 'none',
                          borderBottom: '1px solid var(--gray-100)',
                          cursor: 'pointer',
                          textAlign: 'left'
                        }}
                      >
                        <div className="avatar" style={{ width: '32px', height: '32px', fontSize: '0.75rem' }}>
                          {contact.first_name[0]}{contact.last_name?.[0] || ''}
                        </div>
                        <div>
                          <div style={{ fontWeight: '500' }}>
                            {contact.first_name} {contact.last_name}
                          </div>
                          {contact.company && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                              {contact.company}
                            </div>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Relationship Type</label>
              <select
                value={relationshipType}
                onChange={(e) => setRelationshipType(e.target.value)}
                className="form-input form-select"
              >
                <option value="">Select type...</option>
                {RELATIONSHIP_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="form-input form-textarea"
                placeholder="How do these contacts know each other?"
                rows={3}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || !selectedContact}
            >
              {saving ? 'Saving...' : 'Add Connection'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ConnectionForm;
